import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { garageApi, vehiclesApi } from "../api/endpoints"
import { ApiError } from "../api/client"
import type { JournalEntry, PersonalGarage, VehicleCard, VehicleStatus } from "../types/api"
import { useTelegram } from "../hooks/useTelegram"
import { useI18n } from "../hooks/useI18n"
import { GarageActivationSheet, GarageLifecycleSheet } from "./GarageLifecycleSheets"
import { GarageLogbookSheet, GarageStatusSheet } from "./GarageFeatureSheets"
import { GarageBackdrop } from "./GarageBackdrop"
import "./GarageEntryPage.css"
import { useGarageActivation } from "../hooks/useGarageActivation"
import { normalizePromoInput } from "../utils/promo"
import { openSupportBot } from "../utils/supportBot"
import { completeStarsInvoice } from "../utils/starsPayment"
import { DEMO_PLATE_NORMALIZED, isValidRussianPlate, normalizePlateInput } from "../utils/plate"
import { GARAGE_PREVIEW_VEHICLE } from "./GaragePreviewVehicle"
import { AutoclanPage } from "./AutoclanPage"
import { PartnerPage } from "./PartnerPage"

const DEMO_GARAGE_VIEWS = 99000

export function GarageEntryPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { tg, hapticImpact, hapticNotification, showAlert } = useTelegram()
  const { locale, t } = useI18n()
  const runtimeError = (error: any, fallbackKey: Parameters<typeof t>[0]) => locale === "ru" && error?.message ? error.message : t(fallbackKey)
  const [activationOpen, setActivationOpen] = useState(false)
  const [partnerOpen, setPartnerOpen] = useState(false)
  const [autoclanOpen, setAutoclanOpen] = useState(false)
  const [featureSheet, setFeatureSheet] = useState<"logbook" | "status" | "clean" | null>(null)
  const [journal, setJournal] = useState<JournalEntry[]>([])
  const [statusOptions, setStatusOptions] = useState<VehicleStatus[]>([])
  const [selectedStatusId, setSelectedStatusId] = useState<number | null | undefined>(undefined)
  const [sheetChecked, setSheetChecked] = useState(false)
  const [isCleaning, setIsCleaning] = useState(false)
  const [personalGarage, setPersonalGarage] = useState<PersonalGarage | null>(null)
  const [garageChecked, setGarageChecked] = useState(false)
  const [garageLoadFailed, setGarageLoadFailed] = useState(false)
  const [demoVehicle, setDemoVehicle] = useState<VehicleCard | null>(null)
  const [demoChecked, setDemoChecked] = useState(false)
  const [isAttaching, setIsAttaching] = useState(false)
  const garageRequestId = useRef(0)

  const incomingPromo = useMemo(() => normalizePromoInput(searchParams.get("promo") || ""), [searchParams])
  const incomingAffiliate = useMemo(() => searchParams.get("affiliate") || "", [searchParams])

  const activation = useGarageActivation("", incomingPromo, incomingAffiliate)
  const {
    plate,
    promoCode,
    plateError: error,
    promoValid,
    promoError,
    bonusYears,
    promoAmountStars,
    promoDurationLabel,
    checked: ownershipConfirmed,
    busy: isActivating,
  } = activation
  const isIOS = ["ios", "macos"].includes((tg?.platform || "").toLowerCase())

  const activeUnboundGarage = Boolean(
    personalGarage?.is_active &&
    personalGarage.is_owner &&
    personalGarage.vehicle_id == null,
  )

  const loadPersonalGarage = useCallback(async () => {
    const requestId = ++garageRequestId.current
    setGarageChecked(false)
    setGarageLoadFailed(false)

    try {
      const garage = await garageApi.getMine()
      if (requestId !== garageRequestId.current) return

      if (garage.is_active && garage.is_owner && garage.vehicle_id != null) {
        navigate(`/garage/${garage.vehicle_id}`, { replace: true })
        return
      }

      setPersonalGarage(garage.is_active && garage.is_owner ? garage : null)
    } catch (requestError) {
      if (requestId !== garageRequestId.current) return

      if (requestError instanceof ApiError && requestError.status === 404) {
        setPersonalGarage(null)
        setGarageLoadFailed(false)
      } else {
        setGarageLoadFailed(true)
      }
    } finally {
      if (requestId === garageRequestId.current) setGarageChecked(true)
    }
  }, [navigate])

  useEffect(() => {
    void loadPersonalGarage()

    return () => {
      garageRequestId.current += 1
    }
  }, [loadPersonalGarage])

  useEffect(() => {
    const refreshGarage = () => { void loadPersonalGarage() }
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshGarage()
    }

    tg?.onEvent("activated", refreshGarage)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      tg?.offEvent("activated", refreshGarage)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [loadPersonalGarage, tg])

  useEffect(() => {
    if (!garageChecked || garageLoadFailed) return
    if (activeUnboundGarage) {
      setDemoVehicle(null)
      setDemoChecked(true)
      return
    }

    let cancelled = false
    setDemoChecked(false)
    vehiclesApi.getByPlate(DEMO_PLATE_NORMALIZED)
      .then((card) => {
        if (cancelled) return
        setDemoVehicle({ ...card, display_views: DEMO_GARAGE_VIEWS })
      })
      .catch(() => {
        if (!cancelled) setGarageLoadFailed(true)
      })
      .finally(() => {
        if (!cancelled) setDemoChecked(true)
      })

    return () => { cancelled = true }
  }, [garageChecked, garageLoadFailed, activeUnboundGarage])

  const openActivation = () => {
    hapticImpact("light")
    if (activeUnboundGarage) activation.setPromoCode("")
    setActivationOpen(true)
  }

  const closeActivation = () => {
    setActivationOpen(false)
    activation.reset()
  }

  const openPartner = () => {
    hapticImpact("light")
    setPartnerOpen(true)
  }

  const openAutoclan = () => {
    hapticImpact("light")
    setAutoclanOpen(true)
  }

  const openDispatcherBot = () => {
    openSupportBot(tg, "dispatcher")
  }

  const openLogbook = async () => {
    if (!activeUnboundGarage || personalGarage?.id == null) return
    hapticImpact("light")
    try {
      setJournal(await garageApi.getJournal(personalGarage.id))
      setFeatureSheet("logbook")
    } catch (error: any) {
      showAlert(runtimeError(error, "garageEntry.loadJournalError"))
    }
  }

  const openStatusSheet = async () => {
    if (!activeUnboundGarage || personalGarage?.id == null) return
    hapticImpact("light")
    try {
      setStatusOptions(await garageApi.getStatuses())
      setSelectedStatusId(undefined)
      setFeatureSheet("status")
    } catch (error: any) {
      showAlert(runtimeError(error, "garageEntry.loadStatusesError"))
    }
  }

  const openCleanStart = () => {
    if (!activeUnboundGarage || personalGarage?.id == null) return
    hapticImpact("light")
    setSheetChecked(false)
    setFeatureSheet("clean")
  }

  const handleCleanStart = async () => {
    if (!activeUnboundGarage || personalGarage?.id == null || isCleaning) return
    setIsCleaning(true)
    hapticImpact("medium")
    try {
      if (personalGarage.clean_start_is_free) {
        await garageApi.cleanStart(personalGarage.id)
      } else {
        const invoice = await garageApi.createCleanInvoice(personalGarage.id)
        if (!await completeStarsInvoice(invoice)) return
      }
      setFeatureSheet(null)
      setSheetChecked(false)
      hapticNotification("success")
    } catch (error: any) {
      showAlert(runtimeError(error, "common.error"))
    } finally {
      setIsCleaning(false)
    }
  }

  const togglePrivacy = async () => {
    if (!activeUnboundGarage || personalGarage?.id == null) return
    hapticImpact("light")
    try {
      const updated = await garageApi.updatePrivacy(personalGarage.id, !personalGarage.hide_attention_signals)
      setPersonalGarage(updated)
    } catch (error: any) {
      showAlert(runtimeError(error, "common.error"))
    }
  }

  const handleSuggestStatus = () => {
    openSupportBot(tg, "status")
  }

  const handleActivate = async () => {
    hapticImpact("medium")
    const activated = await activation.activate()
    if (!activated) {
      hapticNotification("error")
      return
    }
    hapticNotification("success")
    navigate(`/garage/${activated.vehicle_id}`, {
      state: { garageNotice: t("garageEntry.activationSuccess"), garageNoticeKind: "activation" },
    })
  }

  const handleAttach = async () => {
    if (!personalGarage?.id || isAttaching || !ownershipConfirmed || error) return
    const normalizedPlate = normalizePlateInput(plate)
    if (!normalizedPlate || !isValidRussianPlate(normalizedPlate)) return

    setIsAttaching(true)
    hapticImpact("medium")
    try {
      const attached = await garageApi.attachVehicle(personalGarage.id, normalizedPlate)
      hapticNotification("success")
      navigate(`/garage/${attached.vehicle_id}`, {
        state: { garageNotice: t("garageEntry.plateAttached"), garageNoticeKind: "activation" },
      })
    } catch (error: any) {
      hapticNotification("error")
      showAlert(runtimeError(error, "garageEntry.attachError"))
    } finally {
      setIsAttaching(false)
    }
  }

  if (!garageChecked || garageLoadFailed || !demoChecked) {
    return <div className="bbf-garage-loading" aria-label={t("common.loading")} />
  }

  const baseVehicle = activeUnboundGarage ? GARAGE_PREVIEW_VEHICLE : demoVehicle
  if (!baseVehicle) {
    return <div className="bbf-garage-loading" aria-label={t("common.loading")} />
  }

  const statusPreview = featureSheet === "status" && selectedStatusId !== undefined
    ? selectedStatusId === null
      ? null
      : statusOptions.find((status) => status.id === selectedStatusId) || baseVehicle.current_status
    : baseVehicle.current_status
  const renderedVehicle = statusPreview === baseVehicle.current_status
    ? baseVehicle
    : { ...baseVehicle, current_status: statusPreview }

  return (
    <div className={`garage-entry-final ${isIOS ? "platform-ios" : "platform-android"}`}>
      <GarageBackdrop
        vehicle={renderedVehicle}
        isIOS={isIOS}
        active={activeUnboundGarage}
        vehicleBound={!activeUnboundGarage}
        privacyOn={Boolean(personalGarage?.hide_attention_signals)}
        activationNote={t("garageEntry.activationNote")}
        onBack={() => navigate(-1)}
        stopDisabled
        startDisabled={false}
        onStart={openActivation}
        actionHandlers={{
          journal: () => void openLogbook(),
          status: () => void openStatusSheet(),
          autoclan: openAutoclan,
          privacy: () => void togglePrivacy(),
          "clean-start": openCleanStart,
          autotrade: () => navigate("/commercial/autotrade"),
          insurance: () => navigate("/commercial/insurance"),
          pitstop: () => navigate("/commercial/pitstop"),
          autohelp: () => navigate("/commercial/techhelp"),
          companion: openPartner,
          "bb-help": openDispatcherBot,
        }}
      >
        {partnerOpen && (
          <PartnerPage embedded onClose={() => setPartnerOpen(false)} />
        )}
        {autoclanOpen && (
          <AutoclanPage embedded onClose={() => setAutoclanOpen(false)} />
        )}
        {featureSheet === "logbook" && (
          <GarageLogbookSheet
            isIOS={isIOS}
            entries={journal}
            onBack={() => setFeatureSheet(null)}
            onReply={() => undefined}
          />
        )}
        {featureSheet === "status" && (
          <GarageStatusSheet
            isIOS={isIOS}
            statuses={statusOptions}
            currentStatusId={null}
            selectedStatusId={selectedStatusId}
            busy={false}
            onSelect={setSelectedStatusId}
            onSuggestOwn={handleSuggestStatus}
            onBack={() => { setSelectedStatusId(undefined); setFeatureSheet(null) }}
            onApply={() => undefined}
          />
        )}
        {featureSheet === "clean" && (
          <GarageLifecycleSheet
            kind="clean"
            isIOS={isIOS}
            checked={sheetChecked}
            busy={isCleaning}
            free={Boolean(personalGarage?.clean_start_is_free)}
            onToggle={() => setSheetChecked((value) => !value)}
            onBack={() => { setFeatureSheet(null); setSheetChecked(false) }}
            onConfirm={() => void handleCleanStart()}
          />
        )}
        {activationOpen && (
          <GarageActivationSheet
            isIOS={isIOS}
            mode={activeUnboundGarage ? "attach" : "activate"}
            plate={plate}
            plateError={error}
            promoCode={activeUnboundGarage ? "" : promoCode}
            promoValid={activeUnboundGarage ? true : promoValid}
            promoError={activeUnboundGarage ? "" : promoError}
            bonusYears={activeUnboundGarage ? 0 : bonusYears}
            promoAmountStars={activeUnboundGarage ? null : promoAmountStars}
            promoDurationLabel={activeUnboundGarage ? null : promoDurationLabel}
            checked={ownershipConfirmed}
            busy={activeUnboundGarage ? isAttaching : isActivating}
            onPlateChange={activation.setPlate}
            onPromoChange={activation.setPromoCode}
            onToggle={() => activation.setChecked(!ownershipConfirmed)}
            onBack={closeActivation}
            onConfirm={() => void (activeUnboundGarage ? handleAttach() : handleActivate())}
          />
        )}
      </GarageBackdrop>
    </div>
  )
}
